import { makeAutoObservable } from 'mobx';
import createStoreContext from '../createStoreContext';
import { LOCAL_STORAGE_KEYS } from 'src/constants/LocalStorageKeys';
import { UserApiResponse } from 'src/types/user';
import { DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER } from 'src/constants/TaskData';
import { MEMORY_LEVELS, MEMORY_STATUS } from 'src/constants/memory';
import { Tag } from 'src/types/tag';
import {
  DEFAULT_DOCK_POSITION,
  DOCK_POSITION,
  DEFAULT_DOCK_VISIBLE,
} from 'src/constants/dock';
import { DockPosition } from 'src/types/dock';
import { DEFAULT_DASHBOARD_TOUR_REQUIRED } from 'src/constants/tour';

const DEFAULT_TASK_FILTERS: TaskFilterSettings = {
  query: '',
  sort: DEFAULT_SORT_ORDER,
  sortField: DEFAULT_SORT_FIELD,
  assignees: [],
  priority: '',
  tags: [],
  dueDateFrom: '',
  dueDateTo: '',
};

const DEFAULT_MEMORY_FILTERS: MemoryFilterSettings = {
  searchText: '',
  status: '',
  level: '',
  tags: [],
};

export interface TaskFilterSettings {
  query: string;
  sort: string;
  sortField: string;
  assignees: UserApiResponse[];
  priority: string;
  tags: Tag[];
  dueDateFrom: string;
  dueDateTo: string;
}

export interface MemoryFilterSettings {
  searchText: string;
  status: MEMORY_STATUS | '';
  level: MEMORY_LEVELS | '';
  tags: Tag[];
}

class UserSettingsStore {
  taskFilters: TaskFilterSettings = { ...DEFAULT_TASK_FILTERS };

  memoryFilters: MemoryFilterSettings = { ...DEFAULT_MEMORY_FILTERS };

  dockPosition: DockPosition = DEFAULT_DOCK_POSITION;

  dockVisible: boolean = DEFAULT_DOCK_VISIBLE;

  isDashboardTourCompleted: boolean = !DEFAULT_DASHBOARD_TOUR_REQUIRED;

  constructor() {
    makeAutoObservable(this);
    this.loadFromLocalStorage();
  }

  private loadFromLocalStorage = () => {
    const taskFilters = localStorage.getItem(LOCAL_STORAGE_KEYS.TASK_FILTERS);

    if (taskFilters) {
      try {
        this.taskFilters = { ...this.taskFilters, ...JSON.parse(taskFilters) };
      } catch (error) {
        console.error('Error parsing task filters from localStorage', error);
      }
    }

    const memoryFilters = localStorage.getItem(
      LOCAL_STORAGE_KEYS.MEMORY_FILTERS,
    );

    if (memoryFilters) {
      try {
        this.memoryFilters = {
          ...this.memoryFilters,
          ...JSON.parse(memoryFilters),
        };
      } catch (error) {
        console.error('Error parsing memory filters from localStorage', error);
      }
    }

    const dockPosition = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCK_POSITION);

    if (
      dockPosition &&
      Object.values(DOCK_POSITION).includes(dockPosition as DOCK_POSITION)
    ) {
      this.dockPosition = dockPosition as DockPosition;
    }

    const dockVisible = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCK_VISIBLE);

    if (dockVisible !== null) {
      this.dockVisible = dockVisible === 'true';
    }

    const dashboardTourCompleted = localStorage.getItem(
      LOCAL_STORAGE_KEYS.DASHBOARD_TOUR_COMPLETED,
    );

    if (dashboardTourCompleted !== null) {
      this.isDashboardTourCompleted = dashboardTourCompleted === 'true';
    }
  };

  private saveTaskFilters = () => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.TASK_FILTERS,
      JSON.stringify(this.taskFilters),
    );
  };

  private saveMemoryFilters = () => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MEMORY_FILTERS,
      JSON.stringify(this.memoryFilters),
    );
  };

  private saveDockPosition = () => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCK_POSITION, this.dockPosition);
  };

  private saveDockVisible = () => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCK_VISIBLE,
      String(this.dockVisible),
    );
  };

  private saveDashboardTourCompleted = () => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DASHBOARD_TOUR_COMPLETED,
      String(this.isDashboardTourCompleted),
    );
  };

  resetFilters = () => {
    this.taskFilters = { ...DEFAULT_TASK_FILTERS };
    this.memoryFilters = { ...DEFAULT_MEMORY_FILTERS };
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TASK_FILTERS);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.MEMORY_FILTERS);
  };

  setTaskQuery = (query: string) => {
    this.taskFilters.query = query;
    this.saveTaskFilters();
  };

  setTaskSort = (sort: string) => {
    this.taskFilters.sort = sort;
    this.saveTaskFilters();
  };

  setTaskSortField = (field: string) => {
    this.taskFilters.sortField = field;
    this.saveTaskFilters();
  };

  setTaskAssignees = (assignees: UserApiResponse[]) => {
    this.taskFilters.assignees = assignees;
    this.saveTaskFilters();
  };

  setTaskPriority = (priority: string) => {
    this.taskFilters.priority = priority;
    this.saveTaskFilters();
  };

  setTaskTags = (tags: Tag[]) => {
    this.taskFilters.tags = tags;
    this.saveTaskFilters();
  };

  setTaskDueDateFrom = (value: string) => {
    this.taskFilters.dueDateFrom = value;
    this.saveTaskFilters();
  };

  setTaskDueDateTo = (value: string) => {
    this.taskFilters.dueDateTo = value;
    this.saveTaskFilters();
  };

  setMemorySearchText = (text: string) => {
    this.memoryFilters.searchText = text;
    this.saveMemoryFilters();
  };

  setMemoryStatus = (status: MEMORY_STATUS | '') => {
    this.memoryFilters.status = status;
    this.saveMemoryFilters();
  };

  setMemoryLevel = (level: MEMORY_LEVELS | '') => {
    this.memoryFilters.level = level;
    this.saveMemoryFilters();
  };

  setMemoryTags = (tags: Tag[]) => {
    this.memoryFilters.tags = tags;
    this.saveMemoryFilters();
  };

  setDockPosition = (position: DockPosition) => {
    if (Object.values(DOCK_POSITION).includes(position)) {
      this.dockPosition = position;
      this.saveDockPosition();
    }
  };

  setDockVisible = (visible: boolean) => {
    this.dockVisible = visible;
    this.saveDockVisible();
  };

  setDashboardTourCompleted = (completed: boolean) => {
    this.isDashboardTourCompleted = completed;
    this.saveDashboardTourCompleted();
  };
}

export const [UserSettingsStoreProvider, useUserSettingsStore] =
  createStoreContext(UserSettingsStore);
export type UserSettingsStoreType = ReturnType<typeof useUserSettingsStore>;
